import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  DialogActions,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ImportSectionValuesResponse, backend, displayOutcomeStatus, outcomeStatusIsVisible } from "Backend";
import { FullName } from "components/FullName";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import RequestStatus from "redux/slices/RequestStatus";
import { P } from "theme/typography";
import ProfileImportErrorTable from "./ProfileImportErrorTable";
import { DisplaySectionOutcome, DisplayValidationOutcome } from "SBR/DisplaySectionOutcome";
import { StageNumber } from "shared/StageNumber";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import { selectFormSections, selectOutcomeEnabledFormSections } from "redux/slices/form.slice";
import { sortedOutcomeStatuses } from "SBR/useSectionValues";
import { selectLensOutcomeStatusConfiguration } from "redux/slices/clientLens";
import { useShowSBR } from "SBR/useShowSBR";
import { REVIEW_STATE_UI } from "SBR/REVIEW_STATE_UI";

type Props = {
  onClose: () => void;
  formId: number;
  onFileChange: (hasFile: boolean) => void;
};

const SectionValuesImport = ({ onClose, formId, onFileChange }: Props) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const showSBR = useShowSBR();
  const [file, setFile] = useState<File | undefined>();
  const [validationStatus, setValidationStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const [importStatus, setImportStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const [importResponse, setImportResponse] = useState<ImportSectionValuesResponse | undefined>();
  const [showInstructions, setShowInstructions] = useState(false);
  const outcomeEnabledSections = useAppSelector(selectOutcomeEnabledFormSections);
  const { sbrEnabled, lensOutcomeStatusConfigurations } = useAppSelector(selectLensOutcomeStatusConfiguration);
  const sections = useAppSelector(selectFormSections);

  const sectionLookup = new Map(sections.map((s) => [s.id, s.name]));

  const { enqueueSnackbar } = useSnackbar();

  const resetFile = () => {
    setValidationStatus(RequestStatus.IDLE);
    if (fileInput.current) {
      fileInput.current.value = "";
    }
    setFile(undefined);
    setImportResponse(undefined);
    onFileChange(false);
  };

  const onFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];

    if (file) {
      setImportResponse(undefined);
      setFile(file);
      onFileChange(true);
      setValidationStatus(RequestStatus.PENDING);
      backend
        .importSectionValuesFromExcel(file, formId, true)
        .then((response) => {
          setImportResponse(response);
          if (response.errors && response.errors.length > 0) {
            setValidationStatus(RequestStatus.FAILED);
          } else {
            setValidationStatus(RequestStatus.SUCCEEDED);
          }
        })
        .catch((error) => {
          setValidationStatus(RequestStatus.FAILED);
          console.error("Failed to import document properties: ", error);
          enqueueSnackbar("Unable to Import", { variant: "error" });
        })
        .finally(() => {
          // Reset the file input to allow re-selection of same updated file
          if (fileInput.current) {
            fileInput.current.value = "";
          }
        });
    }
  };

  const importToProfile = () => {
    if (file) {
      setImportStatus(RequestStatus.PENDING);
      backend
        .importSectionValuesFromExcel(file, formId, false)
        .then((res) => {
          enqueueSnackbar(`${res.recordTotal} Records Imported`, { variant: "success" });
          setImportStatus(RequestStatus.SUCCEEDED);
        })
        .catch(() => {
          setImportStatus(RequestStatus.FAILED);
          enqueueSnackbar("Unable to Import", { variant: "error" });
        })
        .finally(() => {
          resetFile();
          onClose();
        });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-gray-100 rounded-lg p-4 flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <StageNumber className="mb-0" step={1} />
          <p className="font-semibold">Download the Excel Template</p>
        </div>
        <p className="text-sm">
          <Link href={showSBR ? "/Section_Value_Template_SBR.xlsx" : "/Section_Value_Template.xlsx"} target="_blank">
            Download the template file
          </Link>{" "}
          and follow the instructions below to fill in the required fields.
        </p>
        {showInstructions && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <p className="font-semibold">General Rules</p>
              <ul className="list-disc list-inside text-sm">
                <li>
                  <strong>Required columns:</strong> <span className="font-mono">case_ID; section_name</span>
                </li>
                <li>
                  <strong>Case sensitive:</strong> All entries must match exactly, including capitalization.
                </li>
                <li>
                  <strong>One section per row:</strong> If modifying multiple sections for the same case, enter each
                  section on a separate row
                </li>
                {showSBR && (
                  <li>
                    <strong>To modify assignees:</strong> Enter the assignee&apos;s email address in the{" "}
                    <span className="font-mono">review_assignee</span> or <span className="font-mono">qa_assignee</span>{" "}
                    columns
                  </li>
                )}
                <li>
                  <strong>Clearing values:</strong> Enter <span className="font-mono">CLEAR</span> in a column to remove
                  the existing value in the system for that section
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <p className="font-semibold">Section Names</p>
              <p className="text-sm">
                Only sections with outcome enabled can be modified using this import. The following sections are
                available for this lens:
              </p>
              <ul className="list-disc list-inside text-sm">
                {outcomeEnabledSections.map((s) => (
                  <li key={s.id}>{s.name}</li>
                ))}
              </ul>
            </div>
            {showSBR && (
              <div className="flex flex-col gap-2">
                <p className="font-semibold">Review Status</p>
                <p className="text-sm">
                  To update the <span className="font-mono">review_status</span> column, use one of the following
                  values:
                </p>
                <ul className="list-disc list-inside text-sm">
                  <li>Review</li>
                  <li>QA</li>
                  <li>Complete</li>
                </ul>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <p className="font-semibold">Review Outcomes</p>
              <p className="text-sm">
                The following review outcomes are available for this lens. These values must match exactly:
              </p>
              <ul className="list-disc list-inside text-sm">
                {sortedOutcomeStatuses
                  .filter((status) => outcomeStatusIsVisible(status, lensOutcomeStatusConfigurations))
                  .map((outcomeStatus) => (
                    <li key={outcomeStatus}>
                      {displayOutcomeStatus(outcomeStatus, sbrEnabled, lensOutcomeStatusConfigurations)}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
        <div>
          <Button
            size="small"
            onClick={() => setShowInstructions((prev) => !prev)}
            variant="text"
            endIcon={showInstructions ? <ArrowDropUp /> : <ArrowDropDown />}>
            {showInstructions ? "Hide" : "Show"} Instructions
          </Button>
        </div>
      </div>
      <div className="border border-gray-100 rounded-lg p-4 flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <StageNumber className="mb-0" step={2} />
          <p className="font-semibold">Upload Template</p>
        </div>
        <p className="text-sm">Once you&apos;ve filled in the template, click below to upload.</p>
        {/* File select input */}
        <input
          type="file"
          ref={fileInput}
          autoComplete="off"
          tabIndex={-1}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: "none" }}
          onChange={onFile}
        />
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mt: 2, gap: 1 }}>
          {file && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <P variant="smBold">Selected File:</P>
              <P variant="sm">{file.name}</P>
            </Box>
          )}
          <Button
            size="small"
            id="import_section_outcomes_file"
            component="label"
            color="default"
            variant="outlined"
            disabled={validationStatus === RequestStatus.PENDING}
            onClick={() => {
              if (fileInput.current) {
                fileInput.current.click();
              }
            }}>
            {file ? "Change File Selection" : "Choose Excel File"}
          </Button>
          {file && validationStatus === RequestStatus.PENDING && <P variant="sm">Validating file...</P>}
          {validationStatus === RequestStatus.SUCCEEDED && (
            <P variant="sm">{importResponse?.recordTotal} Records found </P>
          )}
        </Box>
        {importResponse?.errors && importResponse.errors.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="error">
              We found {importResponse.errorTotal} {importResponse.errorTotal === 1 ? " error " : " errors "} in the
              file. Please fix the errors and try again.
            </Alert>
            <ProfileImportErrorTable errors={importResponse.errors} />
          </Box>
        )}
        {importResponse?.records && (
          <TableContainer sx={{ maxHeight: 200 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Case ID</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Review Status</TableCell>
                  <TableCell>Review Outcome</TableCell>
                  <TableCell>Review Assignee</TableCell>
                  <TableCell>Review Commentary</TableCell>
                  <TableCell>QA Outcome</TableCell>
                  <TableCell>QA Assignee</TableCell>
                  <TableCell>QA Commentary</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importResponse.records.map((record, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>{record.clientCaseId}</TableCell>
                      <TableCell>{sectionLookup.get(record.sectionId) || ""}</TableCell>
                      <TableCell>{REVIEW_STATE_UI[record.reviewState]?.name || "Unknown"}</TableCell>
                      <TableCell>
                        {record.outcomeStatus ? <DisplaySectionOutcome outcomeStatus={record.outcomeStatus} /> : ""}
                      </TableCell>
                      <TableCell>
                        {record.outcomeAssigneeId ? <FullName userId={record.outcomeAssigneeId} /> : ""}
                      </TableCell>
                      <TableCell>{record.outcomeComment}</TableCell>
                      <TableCell>
                        {record.validationStatus ? (
                          <DisplayValidationOutcome validationStatus={record.validationStatus} />
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell>
                        {record.validationAssigneeId ? <FullName userId={record.validationAssigneeId} /> : ""}
                      </TableCell>
                      <TableCell>{record.validationComment}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
      <DialogActions>
        <Button onClick={onClose} sx={{ marginRight: 1 }}>
          Cancel
        </Button>
        <LoadingButton
          loading={importStatus === RequestStatus.PENDING}
          disabled={validationStatus !== RequestStatus.SUCCEEDED}
          variant="contained"
          onClick={importToProfile}>
          Import
        </LoadingButton>
      </DialogActions>
    </div>
  );
};

export default SectionValuesImport;
